'use client';

import { useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import {
  CheckCircle2,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  User,
  Clock,
  Eye,
  Bookmark,
  Trash2,
  Reply,
  Award
} from 'lucide-react';
import { Badge } from '#/components/ui/badge';
import { Button } from '#/components/ui/button';
import { TimeAgo } from '#/components/time-ago';
import { formatDateKST } from '#/lib/date';
import { RichTextViewer } from '#/components/editor/rich-text-editor';
import QaAnswerForm from './qa-answer-form';
import QaCommentForm from './qa-comment-form';
import {
  toggleQaVote,
  acceptQaAnswer,
  deleteQaQuestion,
  deleteQaAnswer,
  deleteQaComment
} from '#/actions/server/qa';

// ─── 타입 ────────────────────────────────────────────────

interface Author {
  id: string;
  name: string;
  email?: string;
  image?: string | null;
}

interface Comment {
  id: string;
  text: string;
  createdAt: string;
  author: { id: string; name: string } | null;
  replies?: Comment[];
}

interface Answer {
  id: string;
  text: string;
  upvoteCount: number;
  createdAt: string;
  updatedAt: string;
  aiModel: string | null;
  reviewNote: string | null;
  needsRevision: boolean;
  author: Author | null;
  comments?: Comment[];
}

interface Question {
  id: string;
  name: string;
  text: string;
  summary: string | null;
  viewCount: number;
  answerCount: number;
  upvoteCount: number;
  tags: string[];
  subTags: string[];
  ruleTags: string[];
  createdAt: string;
  updatedAt: string;
  published: boolean;
  pinned: boolean;
  locked: boolean;
  author: Author | null;
  acceptedAnswerId: string | null;
  category: {
    id: string;
    name: string;
    slug: string;
    parent?: { id: string; name: string; slug: string } | null;
  } | null;
  comments?: Comment[];
}

interface UserVote {
  questionId: string | null;
  answerId: string | null;
  value: 'UP' | 'DOWN';
}

interface QaDetailContentProps {
  question: Question;
  acceptedAnswer: Answer | null;
  suggestedAnswers: Answer[];
  userId: string | null;
  userVotes: UserVote[];
}

// ─── 투표 버튼 ──────────────────────────────────────────

function VoteButtons({
  questionId,
  answerId,
  upvoteCount,
  currentVote,
  userId
}: {
  questionId?: string;
  answerId?: string;
  upvoteCount: number;
  currentVote: 'UP' | 'DOWN' | null;
  userId: string | null;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [optimisticVote, setOptimisticVote] = useState(currentVote);
  const [optimisticCount, setOptimisticCount] = useState(upvoteCount);

  const handleVote = (value: 'UP' | 'DOWN') => {
    if (!userId) {
      router.push(
        '/login?callbackUrl=' + encodeURIComponent(window.location.pathname)
      );
      return;
    }

    // 낙관적 업데이트
    if (optimisticVote === value) {
      setOptimisticVote(null);
      setOptimisticCount((c) => c + (value === 'UP' ? -1 : 1));
    } else if (optimisticVote) {
      setOptimisticVote(value);
      setOptimisticCount((c) => c + (value === 'UP' ? 2 : -2));
    } else {
      setOptimisticVote(value);
      setOptimisticCount((c) => c + (value === 'UP' ? 1 : -1));
    }

    startTransition(async () => {
      try {
        await toggleQaVote({ questionId, answerId, value });
        router.refresh();
      } catch {
        // 실패 시 롤백
        setOptimisticVote(currentVote);
        setOptimisticCount(upvoteCount);
      }
    });
  };

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => handleVote('UP')}
        disabled={isPending}
        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
          optimisticVote === 'UP'
            ? 'bg-[#104573]/10 text-[#0c3558] dark:text-[#60a3d9]'
            : 'text-muted-foreground hover:bg-[#104573]/[0.05] hover:text-[#104573]'
        }`}
      >
        <ThumbsUp className="w-4 h-4" />
        <span>{optimisticCount > 0 ? optimisticCount : ''}</span>
      </button>
      <button
        onClick={() => handleVote('DOWN')}
        disabled={isPending}
        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
          optimisticVote === 'DOWN'
            ? 'bg-red-100 text-red-700'
            : 'text-muted-foreground hover:bg-[#104573]/[0.05] hover:text-red-600'
        }`}
      >
        <ThumbsDown className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── 삭제 버튼 ──────────────────────────────────────────

function DeleteButton({
  onDelete,
  label = '삭제'
}: {
  onDelete: () => Promise<void>;
  label?: string;
}) {
  const [isPending, startTransition] = useTransition();
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-red-600">정말 삭제?</span>
        <Button
          variant="destructive"
          size="sm"
          className="h-7 text-xs px-2"
          disabled={isPending}
          onClick={() => {
            startTransition(async () => {
              await onDelete();
              setConfirming(false);
            });
          }}
        >
          {isPending ? '삭제 중...' : '확인'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs px-2"
          onClick={() => setConfirming(false)}
        >
          취소
        </Button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-red-600 transition-colors"
    >
      <Trash2 className="w-3.5 h-3.5" />
      <span>{label}</span>
    </button>
  );
}

// ─── 답변 카드 ──────────────────────────────────────────

function AnswerCard({
  answer,
  isAccepted,
  anchorId,
  userId,
  questionAuthorId,
  questionId,
  currentVote,
  locked
}: {
  answer: Answer;
  isAccepted: boolean;
  anchorId: string;
  userId: string | null;
  questionAuthorId: string | null;
  questionId: string;
  currentVote: 'UP' | 'DOWN' | null;
  locked: boolean;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const isQuestionAuthor = userId && questionAuthorId === userId;
  const isAnswerAuthor = userId && answer.author?.id === userId;

  const handleAccept = () => {
    startTransition(async () => {
      try {
        await acceptQaAnswer({
          questionId,
          answerId: answer.id
        });
        router.refresh();
      } catch {}
    });
  };

  const handleDelete = async () => {
    await deleteQaAnswer({ answerId: answer.id });
    router.refresh();
  };

  return (
    <div id={anchorId} className="scroll-mt-24">
      <div
        className={`rounded-2xl border ${
          isAccepted
            ? 'border-green-200 dark:border-green-800 bg-card shadow-sm'
            : 'border-[#104573]/10 bg-card'
        }`}
      >
        {/* 답변 헤더 */}
        <div className="px-6 pt-5 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isAccepted && (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-200 gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                채택됨
              </Badge>
            )}
            {!isAccepted && (
              <Badge variant="secondary" className="gap-1">
                <MessageSquare className="w-3.5 h-3.5" />
                답변
              </Badge>
            )}
            {/* {answer.aiModel && (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                AI: {answer.aiModel}
              </Badge>
            )} */}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="w-3.5 h-3.5" />
              {answer.author?.name || '바른AI'}
            </span>
            <TimeAgo date={answer.createdAt} />
          </div>
        </div>

        {/* 답변 본문 */}
        <div className="px-6 pb-5">
          <RichTextViewer content={answer.text} />

          {answer.reviewNote && (
            <div className="mt-4 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
              <p className="text-xs font-medium text-yellow-800 mb-1">
                📝 감수 의견
              </p>
              <p className="text-sm text-yellow-900">{answer.reviewNote}</p>
            </div>
          )}
        </div>

        {/* 답변 하단: 투표 + 채택 + 삭제 */}
        <div className="px-6 py-3 border-t border-[#104573]/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <VoteButtons
              answerId={answer.id}
              upvoteCount={answer.upvoteCount}
              currentVote={currentVote}
              userId={userId}
            />
            <button className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-amber-600 transition-colors">
              <Bookmark className="w-4 h-4" />
              <span>저장</span>
            </button>
          </div>

          <div className="flex items-center gap-3">
            {/* 질문 작성자만 채택 가능 */}
            {isQuestionAuthor && !locked && (
              <button
                onClick={handleAccept}
                disabled={isPending}
                className={`flex items-center gap-1.5 text-sm transition-colors ${
                  isAccepted
                    ? 'text-green-600 hover:text-green-700'
                    : 'text-muted-foreground hover:text-green-600'
                }`}
              >
                <Award className="w-4 h-4" />
                <span>{isAccepted ? '채택 취소' : '답변 채택'}</span>
              </button>
            )}

            {/* 답변 작성자만 삭제 가능 */}
            {isAnswerAuthor && <DeleteButton onDelete={handleDelete} />}
          </div>
        </div>

        {/* 답변 댓글 */}
        {answer.comments && answer.comments.length > 0 && (
          <div className="px-6 py-4 border-t border-[#104573]/[0.06] bg-[#104573]/[0.03]/50">
            <CommentList
              comments={answer.comments}
              userId={userId}
              questionId={questionId}
            />
          </div>
        )}

        {/* 답변 댓글 작성 */}
        {!locked && (
          <div className="px-6 py-3 border-t border-[#104573]/[0.06] rounded-b-2xl">
            <QaCommentForm
              answerId={answer.id}
              userId={userId}
              placeholder="이 답변에 대한 댓글을 작성하세요..."
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 댓글 목록 ──────────────────────────────────────────

function CommentList({
  comments,
  userId,
  questionId
}: {
  comments: Comment[];
  userId: string | null;
  questionId: string;
}) {
  const router = useRouter();

  const handleDeleteComment = async (commentId: string) => {
    await deleteQaComment({ commentId });
    router.refresh();
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        댓글 {comments.length}개
      </p>
      {comments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          userId={userId}
          questionId={questionId}
          onDelete={handleDeleteComment}
          depth={0}
        />
      ))}
    </div>
  );
}

// ─── 개별 댓글 (대댓글 지원) ────────────────────────────

function CommentItem({
  comment,
  userId,
  questionId,
  onDelete,
  depth
}: {
  comment: Comment;
  userId: string | null;
  questionId: string;
  onDelete: (id: string) => Promise<void>;
  depth: number;
}) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const isAuthor = userId && comment.author?.id === userId;

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div
          className={`${depth > 0 ? 'w-5 h-5' : 'w-6 h-6'} rounded-full ${
            depth > 0 ? 'bg-[#104573]/10' : 'bg-[#104573]/10'
          } flex items-center justify-center flex-shrink-0 mt-0.5`}
        >
          <User
            className={`${depth > 0 ? 'w-3 h-3 text-[#104573]' : 'w-3.5 h-3.5 text-muted-foreground'}`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium text-foreground/80">
              {comment.author?.name || '익명'}
            </span>
            <TimeAgo
              className="text-xs text-muted-foreground"
              date={comment.createdAt}
            />
          </div>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {comment.text}
          </p>

          {/* 댓글 액션: 답글 + 삭제 */}
          <div className="flex items-center gap-3 mt-1">
            {userId && depth < 1 && (
              <button
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-[#104573] transition-colors"
              >
                <Reply className="w-3.5 h-3.5" />
                답글
              </button>
            )}
            {isAuthor && (
              <DeleteButton
                onDelete={() => onDelete(comment.id)}
                label="삭제"
              />
            )}
          </div>

          {/* 대댓글 작성 폼 */}
          {showReplyForm && (
            <div className="mt-2">
              <QaCommentForm
                questionId={questionId}
                parentId={comment.id}
                userId={userId}
                placeholder={`${comment.author?.name || '익명'}님에게 답글...`}
                onSuccess={() => setShowReplyForm(false)}
              />
            </div>
          )}
        </div>
      </div>

      {/* 대댓글 목록 */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="ml-8 space-y-2">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              userId={userId}
              questionId={questionId}
              onDelete={onDelete}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── 메인 컴포넌트 ──────────────────────────────────────

export default function QaDetailContent({
  question,
  acceptedAnswer,
  suggestedAnswers,
  userId,
  userVotes
}: QaDetailContentProps) {
  const router = useRouter();

  // 투표 상태 매핑
  const questionVote =
    userVotes.find((v) => v.questionId === question.id)?.value || null;
  const getAnswerVote = (answerId: string) =>
    userVotes.find((v) => v.answerId === answerId)?.value || null;

  const isQuestionAuthor = userId && question.author?.id === userId;

  const handleDeleteQuestion = async () => {
    await deleteQaQuestion({ questionId: question.id });
    router.push('/qa');
  };

  return (
    <div className="space-y-6">
      {/* 질문 카드 */}
      <div className="rounded-2xl border border-[#104573]/10 bg-card shadow-sm overflow-hidden">
        {/* 질문 헤더 */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-[#104573]/10 text-[#0c3558] dark:text-[#60a3d9] hover:bg-[#104573]/20 text-xs">
              질문
            </Badge>
            {question.pinned && (
              <Badge
                variant="outline"
                className="text-xs text-amber-600 border-amber-300"
              >
                📌 고정
              </Badge>
            )}
            {question.locked && (
              <Badge
                variant="outline"
                className="text-xs text-red-500 border-red-300"
              >
                🔒 잠김
              </Badge>
            )}
          </div>

          {/* 질문 제목 */}
          <h1 className="text-xl sm:text-2xl font-bold text-foreground leading-snug mb-4">
            {question.name}
          </h1>

          {/* 메타 정보 */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {question.author?.name || '바른AI'}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatDateKST(question.createdAt)}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              조회 {question.viewCount.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              답변 {question.answerCount}
            </span>
          </div>
        </div>

        {/* 태그 */}
        {question.tags.length > 0 && (
          <div className="px-6 pb-4 flex flex-wrap gap-1.5">
            {question.tags.map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="text-xs bg-[#104573]/[0.05] text-muted-foreground"
              >
                #{tag}
              </Badge>
            ))}
          </div>
        )}

        {/* 질문 본문 */}
        {question.text && (
          <div className="px-6 pb-5">
            <RichTextViewer content={question.text} />
          </div>
        )}

        {/* 질문 하단: 투표 + 삭제 */}
        <div className="px-6 py-3 border-t border-[#104573]/[0.06] flex items-center justify-between">
          <VoteButtons
            questionId={question.id}
            upvoteCount={question.upvoteCount}
            currentVote={questionVote}
            userId={userId}
          />
          {isQuestionAuthor && (
            <DeleteButton onDelete={handleDeleteQuestion} label="질문 삭제" />
          )}
        </div>

        {/* 질문 댓글 */}
        {question.comments && question.comments.length > 0 && (
          <div className="px-6 py-4 border-t border-[#104573]/[0.06] bg-[#104573]/[0.03]/50">
            <CommentList
              comments={question.comments}
              userId={userId}
              questionId={question.id}
            />
          </div>
        )}

        {/* 질문 댓글 작성 */}
        {!question.locked && (
          <div className="px-6 py-3 border-t border-[#104573]/[0.06]">
            <QaCommentForm
              questionId={question.id}
              userId={userId}
              placeholder="이 질문에 대한 댓글을 작성하세요..."
            />
          </div>
        )}
      </div>

      {/* 채택된 답변 */}
      {acceptedAnswer && (
        <AnswerCard
          answer={acceptedAnswer}
          isAccepted={true}
          anchorId="acceptedAnswer"
          userId={userId}
          questionAuthorId={question.author?.id || null}
          questionId={question.id}
          currentVote={getAnswerVote(acceptedAnswer.id)}
          locked={question.locked}
        />
      )}

      {/* 기타 답변 */}
      {suggestedAnswers.length > 0 && (
        <div className="space-y-4">
          {!acceptedAnswer && (
            <h2 className="text-lg font-semibold text-foreground">답변</h2>
          )}
          {acceptedAnswer && (
            <h2 className="text-lg font-semibold text-foreground">
              다른 답변 ({suggestedAnswers.length})
            </h2>
          )}
          {suggestedAnswers.map((answer, idx) => (
            <AnswerCard
              key={answer.id}
              answer={answer}
              isAccepted={false}
              anchorId={`suggestedAnswer${idx + 1}`}
              userId={userId}
              questionAuthorId={question.author?.id || null}
              questionId={question.id}
              currentVote={getAnswerVote(answer.id)}
              locked={question.locked}
            />
          ))}
        </div>
      )}

      {/* 답변 작성 폼 */}
      <QaAnswerForm
        questionId={question.id}
        userId={userId}
        locked={question.locked}
      />
    </div>
  );
}
